import React from 'react'

type PagingProps = {
  totalPages: number
  currentPage: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  scrollToResults: () => unknown
}
export const Paging = ({ totalPages, currentPage, setPage, scrollToResults }: PagingProps): JSX.Element => {
  const setPageAndScroll = (page: number) => () => {
    if (page <= 0) return
    if (page > totalPages) return

    setPage(page)
    scrollToResults()
  }

  if (totalPages <= 1) return <></>

  const pageItems: Array<number | 'ellipsis'> = []
  const start = Math.max(2, currentPage - 1)
  const end = Math.min(totalPages - 1, currentPage + 1)

  pageItems.push(1)

  if (start > 2) pageItems.push('ellipsis')
  for (let page = start; page <= end; page += 1) {
    pageItems.push(page)
  }
  if (end < totalPages - 1) pageItems.push('ellipsis')
  if (totalPages > 1) pageItems.push(totalPages)

  const buttonBase =
    'min-h-[38px] min-w-[38px] rounded-full border px-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-royal-indigo/30'

  return (
    <nav className="mx-auto mt-8 flex w-full max-w-4xl items-center justify-between gap-3 rounded-2xl border border-royal-indigo/20 bg-white px-3 py-2 shadow-[0_1px_6px_rgba(57,73,171,0.12)] sm:px-4">
      <button
        type="button"
        onClick={setPageAndScroll(currentPage - 1)}
        disabled={currentPage === 1}
        className={`${buttonBase} ${
          currentPage === 1
            ? 'cursor-not-allowed border-soft-lavender/35 text-soft-lavender'
            : 'border-royal-indigo/35 text-royal-indigo hover:bg-royal-indigo/10'
        }`}
        aria-label="Go to previous page"
      >
        Prev
      </button>

      <ul className="flex min-w-0 flex-1 items-center justify-center gap-1.5 sm:gap-2">
        {pageItems.map((item, index) => {
          if (item === 'ellipsis') {
            return (
              <li key={`ellipsis-${index}`} className="px-1 text-sm font-semibold text-soft-lavender">
                ...
              </li>
            )
          }
          const isCurrent = item === currentPage
          return (
            <li key={`page-${item}`}>
              <button
                type="button"
                onClick={setPageAndScroll(item)}
                className={`${buttonBase} ${
                  isCurrent
                    ? 'border-royal-indigo bg-royal-indigo text-white'
                    : 'border-soft-lavender/35 text-royal-indigo hover:border-royal-indigo/45 hover:bg-royal-indigo/10 hover:text-royal-indigo'
                }`}
                aria-label={`Go to page ${item}`}
                aria-current={isCurrent ? 'page' : undefined}
              >
                {item}
              </button>
            </li>
          )
        })}
      </ul>

      <button
        type="button"
        onClick={setPageAndScroll(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`${buttonBase} ${
          currentPage === totalPages
            ? 'cursor-not-allowed border-soft-lavender/35 text-soft-lavender'
            : 'border-royal-indigo/35 text-royal-indigo hover:bg-royal-indigo/10'
        }`}
        aria-label="Go to next page"
      >
        Next
      </button>
    </nav>
  )
}
